// src/components/public/ExchangeRateWidget.jsx
import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { RefreshCw } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const ExchangeRateWidget = forwardRef((props, ref) => {
  const { t } = useLanguage();
  const [rates, setRates] = useState({
    gelToAmd: null,
    amdToGel: null,
    buyRate: null,
    sellRate: null,
    lastUpdated: null,
    isLoading: false,
    error: null
  });

  // Function to fetch exchange rates from your backend
  const fetchExchangeRates = async () => {
    setRates(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const apiUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/exchange-rates/`;
      
      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      setRates({
        gelToAmd: data.buyRate || data.gelToAmd,
        amdToGel: data.amdToGel,
        buyRate: data.buyRate,
        sellRate: data.sellRate,
        lastUpdated: new Date(),
        isLoading: false,
        error: null
      });
    } catch (error) {
      console.error('Failed to fetch exchange rates:', error);
      setRates(prev => ({
        ...prev,
        isLoading: false,
        error: t('failedToFetchRates')
      }));
    }
  };

  // Expose the sellRate via ref
  useImperativeHandle(ref, () => ({
    getSellRate: () => rates.sellRate || rates.gelToAmd,
    getRates: () => rates
  }));

  // Fetch rates on initial load
  useEffect(() => {
    fetchExchangeRates();
    
    // Auto-refresh every hour
    const interval = setInterval(fetchExchangeRates, 3600000);
    
    return () => clearInterval(interval);
  }, []);

  // Format the rates for display
  const formatRate = (rate) => {
    return rate ? rate.toFixed(4) : '---';
  };

  // Format the last updated time
  const formatLastUpdated = () => {
    if (!rates.lastUpdated) return t('notAvailable');
    
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(rates.lastUpdated);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-medium text-gray-800">{t('exchangeRates')}</h3>
        <button 
          onClick={fetchExchangeRates}
          disabled={rates.isLoading}
          className={`p-2 rounded-full ${rates.isLoading ? 'bg-gray-200' : 'bg-primary-100 hover:bg-primary-200'} transition-colors`}
          title={t('refreshRates')}
        >
          <RefreshCw className={`h-4 w-4 text-primary-600 ${rates.isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      
      {rates.error ? (
        <div className="text-red-500 text-sm mb-2">{rates.error}</div>
      ) : null}
      
      <div className="space-y-2">
        <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
          <span className="text-sm text-gray-600">1 GEL = </span>
          <span className="font-medium">{rates.sellRate || formatRate(rates.gelToAmd)} AMD</span>
        </div>
        
        
      </div>
      
      <div className="mt-3 text-xs text-gray-500 text-right">
        {t('lastUpdated')}: {formatLastUpdated()}
      </div>
    </div>
  );
});

ExchangeRateWidget.displayName = 'ExchangeRateWidget';

export default ExchangeRateWidget;